<template>
  <div class="article-summary p-3" :class="{ 'fullscreen-mode': isFullscreen }" :style="{ height: isFullscreen ? '100vh' : (height ? height + 'px' : '100%'), overflowY: 'auto' }" ref="readerRef">
    <div class="article-header-container">
      <a 
        v-if="article" 
        :href="article.link || '#'" 
        target="_blank"
        @click="$emit('open-original', article)"
      >
        {{ article.title || "" }}           
      </a>
      <div class="article-actions">
        <button v-if="article?.link || article?.url" class="view-md-button" @click="fetchAndShowMarkdown(article)" :disabled="isLoadingMd">
          {{ isLoadingMd ? '⏳ 加载中...' : '📄 全文阅读' }}
        </button>
        <button v-if="article?.title" class="play-button" @click="$emit('play', article)">
          ▶ 语音播报
        </button>
        <button v-if="article?.title" class="ai-review-button" @click="$emit('ai-review', article)">
          🤖 AI点评
        </button>
        <button v-if="markdownHtml" class="fullscreen-button" @click="isFullscreen = !isFullscreen" :title="isFullscreen ? '退出全屏' : '全屏阅读'">
          {{ isFullscreen ? '⤡ 退出' : '⤢ 全屏' }}
        </button>
      </div>
    </div>
    <div v-if="mdError" class="alert alert-danger mt-3 p-2">{{ mdError }}</div>
    <div v-if="markdownHtml" class="markdown-body mt-3 p-3 border rounded" :class="{ 'bg-dark text-light': themeStore.currentTheme === 'dark', 'bg-light text-dark': themeStore.currentTheme === 'light' }" v-html="markdownHtml"></div>
    <div v-else-if="!mdError" v-html="article?.excerpt || ' '"></div>
    <audio ref="audioPlayer" controls style="display:none;"></audio>
  </div>
</template>

<script setup>
/* eslint-disable no-undef */
/* eslint-disable no-unused-vars */
import { ref, defineExpose, watch } from 'vue';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { useThemeStore } from '@/stores/themeStore';

const props = defineProps({
    article: Object,
    height: { type: Number, default: 300 }
});

const themeStore = useThemeStore();
const isFullscreen = ref(false);

defineEmits(['open-original', 'play', 'ai-review']);

const audioPlayer = ref(null);

const playAudio = (url) => {
    if (audioPlayer.value) {
        audioPlayer.value.src = url;
        audioPlayer.value.play();
    }
};

const isLoadingMd = ref(false);
const markdownHtml = ref('');
const mdError = ref('');
let abortController = null;

// Clear markdown when article changes
watch(() => props.article, () => {
    markdownHtml.value = '';
    mdError.value = '';
    isLoadingMd.value = false;
    isFullscreen.value = false;
    if (abortController) {
        abortController.abort();
        abortController = null;
    }
});

const fetchAndShowMarkdown = async (article) => {
    const url = article.link || article.url;
    if (!url) return;
    
    // Toggle off if already showing
    if (markdownHtml.value || mdError.value) {
        markdownHtml.value = '';
        mdError.value = '';
        return;
    }
    
    if (abortController) {
        abortController.abort();
    }
    abortController = new AbortController();
    const timeoutId = setTimeout(() => abortController.abort(), 30000);
    
    isLoadingMd.value = true;
    mdError.value = '';
    try {
        const response = await fetch('/api/Url2Md', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ url, screenshot: false }),
            signal: abortController.signal
        });
        
        const result = await response.json();
        if (result.success && result.markdown) {
            const rawHtml = await marked.parse(result.markdown);
            markdownHtml.value = DOMPurify.sanitize(rawHtml, {
                ALLOWED_URI_REGEXP: /^(https?|mailto|tel):/i,
                ADD_ATTR: ['referrerpolicy']
            });
        } else {
            mdError.value = '获取原文失败: ' + (result.error || result.Error || '未知错误');
        }
    } catch (err) {
        if (err.name === 'AbortError') {
            mdError.value = '抓取超时（30s）';
        } else {
            mdError.value = '请求失败: ' + err.message;
        }
    } finally {
        clearTimeout(timeoutId);
        isLoadingMd.value = false;
    }
};

defineExpose({ playAudio });
</script>

<style scoped>
@import '@/assets/styles/AppLayout.css';

.view-md-button,
.fullscreen-button {
  transition: all 0.2s ease;
}

.view-md-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.fullscreen-mode {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh !important;
  z-index: 9999;
  background-color: var(--primary-bg);
  padding: 20px !important;
  box-sizing: border-box;
}
.fullscreen-mode .markdown-body {
  height: calc(100vh - 80px);
  overflow-y: auto;
}
.markdown-body {
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif;
  line-height: 1.6;
  overflow-wrap: break-word;
}
.markdown-body img {
  max-width: 100%;
  box-sizing: content-box;
}
</style>
